import { useState } from "react";
import {
  Heart, Briefcase, Scale, Zap, Wand2, ShieldCheck, Sparkles,
  Save, Info, Smile, Loader2, MessageCircle, Upload,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import AssistantAvatar, { PRESET_ICON_KEYS } from "@/components/AssistantAvatar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DEFAULT_BEHAVIOR = {
  mode: "guided",
  system_prompt: "",
  tone: "neutral",
  custom_persona: "",
  address_form: "siz",
  grounding: "balanced",
  verbosity: "medium",
  answer_format: "auto",
  emoji: false,
  welcome_message: "",
  forbidden_topics: "",
  refusal_message: "",
  suggested_questions: [],
  widget_color: "#0a0a0a",
  widget_position: "right",
  icon: { type: "preset", value: "bot" },
};

const WIDGET_COLORS = ["#0a0a0a", "#2563eb", "#059669", "#7c3aed", "#dc2626", "#ea580c"];
const EMOJI_SUGGESTIONS = ["🤖", "💬", "📚", "✨", "🎧", "🛍️", "💡", "❤️", "🚀", "🏦", "🩺", "⚖️"];

const TONES = [
  { id: "warm", label: "Sevecen & Samimi", desc: "Sıcak, yakın dil", icon: Heart },
  { id: "professional", label: "Profesyonel & Ciddi", desc: "Kurumsal, resmi", icon: Briefcase },
  { id: "neutral", label: "Nötr & Dengeli", desc: "Varsayılan üslup", icon: Scale },
  { id: "energetic", label: "Enerjik & Rahat", desc: "Sohbet havasında", icon: Zap },
  { id: "custom", label: "Özel", desc: "Kendi talimatın", icon: Wand2 },
];

const GROUNDINGS = [
  { id: "strict", label: "Katı", desc: "Sadece belgelerdeki bilgi, asla yorum katmaz", icon: ShieldCheck },
  { id: "balanced", label: "Dengeli", desc: "Ağırlıklı belgeler + kısa bağlayıcı açıklamalar", icon: Scale },
  { id: "flexible", label: "Esnek", desc: "Belge yoksa genel bilgiyle destekler, kaynağı belirtir", icon: Sparkles },
];

const VERBOSITY = [
  { id: "short", label: "Kısa & öz" },
  { id: "medium", label: "Orta" },
  { id: "detailed", label: "Detaylı" },
];

const FORMATS = [
  { id: "auto", label: "Otomatik" },
  { id: "bullets", label: "Madde madde" },
  { id: "paragraph", label: "Paragraf" },
];

function SectionCard({ children }) {
  return <div className="soft-card p-5">{children}</div>;
}

function SelectableCard({ active, icon: Icon, label, desc, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all ${
        active
          ? "border-foreground bg-foreground/[0.03] ring-1 ring-foreground"
          : "border-border hover:border-foreground/30 hover:bg-secondary/40"
      }`}
    >
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
        active ? "bg-foreground text-background" : "bg-secondary text-foreground"
      }`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold leading-tight">{label}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
      </div>
    </button>
  );
}

export default function BehaviorSettings({ assistant, onSaved }) {
  const cfg = assistant.config_draft || {};
  const [behavior, setBehavior] = useState({ ...DEFAULT_BEHAVIOR, ...(cfg.behavior || {}) });
  const [language, setLanguage] = useState(cfg.answer?.language || "tr");
  const [fallback, setFallback] = useState(
    cfg.answer?.fallback_message || "Bu konuda kaynaklarda yeterli bilgi bulamadım."
  );
  const [suggested, setSuggested] = useState((cfg.behavior?.suggested_questions || []).join("\n"));
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [iconMode, setIconMode] = useState((cfg.behavior?.icon?.type) || "preset");
  const [uploadingIcon, setUploadingIcon] = useState(false);

  const set = (patch) => setBehavior((b) => ({ ...b, ...patch }));
  const setIcon = (icon) => set({ icon });
  const advanced = behavior.mode === "advanced";

  const chooseIconMode = (m) => {
    setIconMode(m);
    if (m === "preset" && behavior.icon?.type !== "preset") setIcon({ type: "preset", value: "bot" });
    if (m === "emoji" && behavior.icon?.type !== "emoji") setIcon({ type: "emoji", value: "🤖" });
  };

  const onIconFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingIcon(true);
    try {
      const { file_id } = await api.uploadAssistantIcon(assistant.id, f);
      setIcon({ type: "image", value: file_id });
      setIconMode("image");
      toast.success("Logo yüklendi — kaydetmeyi unutmayın");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo yüklenemedi");
    } finally {
      setUploadingIcon(false);
      e.target.value = "";
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { questions } = await api.suggestQuestions(assistant.id);
      if (!questions?.length) { toast.error("Soru üretilemedi"); return; }
      setSuggested((prev) => {
        const existing = prev.split("\n").map((s) => s.trim()).filter(Boolean);
        const unique = [...new Set([...existing, ...questions])].slice(0, 6);
        return unique.join("\n");
      });
      toast.success(`${questions.length} soru önerisi eklendi`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Üretilemedi");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const suggestedArr = suggested
        .split("\n").map((s) => s.trim()).filter(Boolean).slice(0, 6);
      const merged = {
        ...cfg,
        behavior: { ...behavior, suggested_questions: suggestedArr },
        answer: {
          ...(cfg.answer || {}),
          language,
          fallback_message: fallback,
          require_citations: cfg.answer?.require_citations ?? true,
        },
      };
      const updated = await api.updateAssistantConfig(assistant.id, merged);
      toast.success("Davranış ayarları kaydedildi");
      onSaved?.(updated);
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const published = assistant.status === "published";

  return (
    <div className="space-y-6" data-testid="behavior-settings">
      {/* Asistan ikonu */}
      <SectionCard>
        <h3 className="font-bold">Asistan ikonu</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Her yerde (hosted chat, widget balonu, panel, admin) kullanılacak asistan kimliği.
        </p>
        <div className="mt-4 flex items-center gap-4">
          <AssistantAvatar icon={behavior.icon} color={behavior.widget_color} size={56} />
          <div className="inline-flex rounded-full border border-border p-1">
            {[["preset", "Hazır ikon"], ["emoji", "Emoji"], ["image", "Logo yükle"]].map(([v, l]) => (
              <button
                key={v}
                type="button"
                data-testid={`icon-mode-${v}`}
                onClick={() => chooseIconMode(v)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  iconMode === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {iconMode === "preset" && (
          <div className="mt-4 flex flex-wrap gap-2">
            {PRESET_ICON_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                data-testid={`icon-preset-${k}`}
                onClick={() => setIcon({ type: "preset", value: k })}
                className={`rounded-xl border p-2 transition-all ${
                  behavior.icon?.type === "preset" && behavior.icon?.value === k
                    ? "border-foreground ring-1 ring-foreground"
                    : "border-border hover:border-foreground/30"
                }`}
              >
                <AssistantAvatar icon={{ type: "preset", value: k }} color={behavior.widget_color} size={32} />
              </button>
            ))}
          </div>
        )}

        {iconMode === "emoji" && (
          <div className="mt-4 space-y-3">
            <Input
              data-testid="icon-emoji-input"
              value={behavior.icon?.type === "emoji" ? behavior.icon.value : ""}
              onChange={(e) => setIcon({ type: "emoji", value: [...e.target.value].slice(0, 2).join("") })}
              placeholder="🤖"
              className="w-24 rounded-xl text-center text-2xl"
            />
            <div className="flex flex-wrap gap-2">
              {EMOJI_SUGGESTIONS.map((em) => (
                <button
                  key={em}
                  type="button"
                  data-testid={`icon-emoji-${em}`}
                  onClick={() => setIcon({ type: "emoji", value: em })}
                  className="text-2xl transition-transform hover:scale-125"
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
        )}

        {iconMode === "image" && (
          <div className="mt-4">
            <label
              data-testid="icon-upload-btn"
              className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-4 py-2 text-sm transition-colors hover:bg-secondary/50"
            >
              {uploadingIcon
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...</>
                : <><Upload className="h-4 w-4" /> Logo seç (PNG, SVG, JPG · maks 2MB)</>}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                className="hidden"
                onChange={onIconFile}
                disabled={uploadingIcon}
              />
            </label>
            {behavior.icon?.type === "image" && (
              <p className="mt-2 text-xs text-emerald-600">Logo hazır ✓ Değişikliği "Kaydet" ile onaylayın.</p>
            )}
          </div>
        )}
      </SectionCard>

      {/* Yapılandırma modu */}
      <SectionCard>
        <h3 className="font-bold">Yapılandırma modu</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Asistanın kişiliğini hazır seçeneklerle mi, yoksa kendi özel sistem promptunla mı tanımlamak istediğini seç.
        </p>
        <div className="mt-3 inline-flex flex-wrap rounded-full border border-border p-1">
          {[["guided", "Kılavuzlu (seçenekli)"], ["advanced", "Gelişmiş (özel prompt)"]].map(([v, l]) => (
            <button
              key={v}
              type="button"
              data-testid={`behavior-mode-${v}`}
              onClick={() => set({ mode: v })}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                behavior.mode === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {advanced && (
          <div className="mt-4">
            <Label className="text-xs font-medium text-muted-foreground">
              Gelişmiş Yönergeler (Özel Sistem Promptu)
            </Label>
            <Textarea
              data-testid="system-prompt-input"
              value={behavior.system_prompt}
              onChange={(e) => set({ system_prompt: e.target.value })}
              placeholder={"Asistanın rolünü, kişiliğini ve davranış kurallarını buraya yapıştırın.\n\nÖrn: Sen deneyimli bir mentorsun. Mentee'ye sabırlı, cesaretlendirici ve net konuş..."}
              className="mt-1.5 min-h-[240px] rounded-xl font-mono text-sm leading-relaxed"
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Bu metin asistanın ana kişilik talimatı olur. Kaynağa bağlılık, atıf ([n]) ve "bilmiyorum" mantığı her koşulda korunur; bu modda yukarıdaki üslup/hitap/uzunluk/emoji seçenekleri devre dışı kalır.
            </p>
          </div>
        )}
      </SectionCard>

      {/* Üslup */}
      <SectionCard>
        <h3 className="font-bold">Konuşma üslubu</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">Asistanın tonu ve kişiliği.</p>
        {advanced && (
          <p className="mt-2 text-xs text-amber-600">Gelişmiş mod açık — üslup ayarları özel sistem promptu tarafından yönetiliyor.</p>
        )}
        <div className={`mt-4 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3 ${advanced ? "pointer-events-none opacity-40" : ""}`}>
          {TONES.map((t) => (
            <SelectableCard
              key={t.id}
              testId={`tone-${t.id}`}
              active={behavior.tone === t.id}
              icon={t.icon}
              label={t.label}
              desc={t.desc}
              onClick={() => set({ tone: t.id })}
            />
          ))}
        </div>
        {behavior.tone === "custom" && !advanced && (
          <div className="mt-4">
            <Label className="text-xs font-medium text-muted-foreground">Özel kişilik talimatı</Label>
            <Textarea
              data-testid="custom-persona-input"
              value={behavior.custom_persona}
              onChange={(e) => set({ custom_persona: e.target.value })}
              placeholder="Örn: Genç, esprili ama saygılı bir marka temsilcisi gibi konuş."
              className="mt-1.5 min-h-[80px] rounded-xl"
            />
          </div>
        )}
      </SectionCard>

      {/* Hitap + Uzunluk + Dil + Emoji */}
      <SectionCard>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Hitap */}
          <div className={advanced ? "pointer-events-none opacity-40" : ""}>
            <h3 className="font-bold">Hitap şekli</h3>
            <div className="mt-3 inline-flex rounded-full border border-border p-1">
              {[["sen", "Sen (samimi)"], ["siz", "Siz (resmi)"]].map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  data-testid={`address-${val}`}
                  onClick={() => set({ address_form: val })}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    behavior.address_form === val ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Uzunluk */}
          <div className={advanced ? "pointer-events-none opacity-40" : ""}>
            <h3 className="font-bold">Cevap uzunluğu</h3>
            <div className="mt-3 inline-flex rounded-full border border-border p-1">
              {VERBOSITY.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  data-testid={`verbosity-${v.id}`}
                  onClick={() => set({ verbosity: v.id })}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    behavior.verbosity === v.id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Yanıt formatı */}
          <div className={advanced ? "pointer-events-none opacity-40" : ""}>
            <h3 className="font-bold">Yanıt formatı</h3>
            <div className="mt-3 inline-flex rounded-full border border-border p-1">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`format-${f.id}`}
                  onClick={() => set({ answer_format: f.id })}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    behavior.answer_format === f.id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dil */}
          <div>
            <h3 className="font-bold">Yanıt dili</h3>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="language-select" className="mt-3 w-full rounded-xl sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tr">Türkçe</SelectItem>
                <SelectItem value="en">İngilizce</SelectItem>
                <SelectItem value="auto">Soruya göre otomatik</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Emoji */}
          <div className={advanced ? "pointer-events-none opacity-40" : ""}>
            <h3 className="font-bold">Emoji kullanımı</h3>
            <div
              role="button"
              tabIndex={0}
              data-testid="emoji-row"
              onClick={() => set({ emoji: !behavior.emoji })}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); set({ emoji: !behavior.emoji }); } }}
              className="mt-3 flex cursor-pointer select-none items-center gap-3 rounded-xl border border-border px-4 py-3"
            >
              <Smile className="h-4.5 w-4.5 text-muted-foreground" />
              <span className="flex-1 text-sm">Yanıtlarda emoji kullan</span>
              <Switch
                data-testid="emoji-switch"
                checked={behavior.emoji}
                className="pointer-events-none"
                tabIndex={-1}
              />
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Kaynağa bağlılık */}
      <SectionCard>
        <h3 className="font-bold">Kaynağa bağlılık</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">Cevabın belgelere ne kadar sadık kalacağı.</p>
        <div className="mt-4 grid gap-2.5 sm:grid-cols-3">
          {GROUNDINGS.map((g) => (
            <SelectableCard
              key={g.id}
              testId={`grounding-${g.id}`}
              active={behavior.grounding === g.id}
              icon={g.icon}
              label={g.label}
              desc={g.desc}
              onClick={() => set({ grounding: g.id })}
            />
          ))}
        </div>
      </SectionCard>

      {/* Yasaklı konular */}
      <SectionCard>
        <h3 className="font-bold">Yasaklı konular</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Bu konular sorulduğunda asistan cevap vermez, kibarca reddeder. Her satıra veya virgülle bir konu yazın.
        </p>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Yasaklı konular / anahtar kelimeler</Label>
            <Textarea
              data-testid="forbidden-topics-input"
              value={behavior.forbidden_topics}
              onChange={(e) => set({ forbidden_topics: e.target.value })}
              placeholder={"siyaset\nrakip firmalar\nkişisel tıbbi tavsiye"}
              className="mt-1.5 min-h-[80px] rounded-xl"
            />
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Ret mesajı</Label>
            <Textarea
              data-testid="refusal-message-input"
              value={behavior.refusal_message}
              onChange={(e) => set({ refusal_message: e.target.value })}
              placeholder="Üzgünüm, bu konuda size yardımcı olamıyorum."
              className="mt-1.5 min-h-[64px] rounded-xl"
            />
          </div>
        </div>
      </SectionCard>

      {/* Mesajlar */}
      <SectionCard>
        <h3 className="font-bold">Mesajlar & öneriler</h3>
        <div className="mt-4 space-y-4">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Karşılama mesajı (hosted chat)</Label>
            <Textarea
              data-testid="welcome-message-input"
              value={behavior.welcome_message}
              onChange={(e) => set({ welcome_message: e.target.value })}
              placeholder="Merhaba! Size nasıl yardımcı olabilirim?"
              className="mt-1.5 min-h-[64px] rounded-xl"
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs font-medium text-muted-foreground">Önerilen sorular (her satıra bir soru, en fazla 6)</Label>
              <Button
                data-testid="generate-questions-btn"
                type="button"
                variant="outline"
                size="sm"
                onClick={generate}
                disabled={generating}
                className="h-7 rounded-full text-xs"
              >
                {generating
                  ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Üretiliyor...</>
                  : <><Sparkles className="mr-1 h-3.5 w-3.5" /> Belgelerden üret</>}
              </Button>
            </div>
            <Textarea
              data-testid="suggested-questions-input"
              value={suggested}
              onChange={(e) => setSuggested(e.target.value)}
              placeholder={"İade nasıl yapılır?\nKargo ne zaman gelir?\nÇalışma saatleriniz nedir?"}
              className="mt-1.5 min-h-[96px] rounded-xl"
            />
            <p className="mt-1 text-xs text-muted-foreground">Hosted chat açılışında tıklanabilir çip olarak gösterilir. "Belgelerden üret" ile yüklü içeriğe göre otomatik doldur.</p>
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">"Bilmiyorum" mesajı</Label>
            <Textarea
              data-testid="fallback-message-input"
              value={fallback}
              onChange={(e) => setFallback(e.target.value)}
              placeholder="Bu konuda kaynaklarda yeterli bilgi bulamadım."
              className="mt-1.5 min-h-[64px] rounded-xl"
            />
          </div>
        </div>
      </SectionCard>

      {/* Widget görünümü */}
      <SectionCard>
        <h3 className="font-bold">Widget görünümü</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Web sitesi sohbet balonunun rengi ve konumu. "Yayın & API" sekmesindeki gömme koduna otomatik yansır.
        </p>
        <div className="mt-4 grid gap-6 md:grid-cols-2">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Balon rengi</Label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {WIDGET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`widget-color-${c}`}
                  onClick={() => set({ widget_color: c })}
                  style={{ backgroundColor: c }}
                  className={`h-8 w-8 rounded-full transition-transform ${
                    behavior.widget_color === c ? "scale-110 ring-2 ring-foreground ring-offset-2" : "border border-border"
                  }`}
                  aria-label={`Renk ${c}`}
                />
              ))}
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs">
                <input
                  type="color"
                  data-testid="widget-color-custom"
                  value={behavior.widget_color}
                  onChange={(e) => set({ widget_color: e.target.value })}
                  className="h-5 w-5 cursor-pointer border-0 bg-transparent p-0"
                />
                Özel
              </label>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Konum</Label>
            <div className="mt-2 inline-flex rounded-full border border-border p-1">
              {[["right", "Sağ alt"], ["left", "Sol alt"]].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`widget-pos-${v}`}
                  onClick={() => set({ widget_position: v })}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    behavior.widget_position === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Canlı önizleme */}
        <div className="mt-5">
          <Label className="text-xs font-medium text-muted-foreground">Önizleme</Label>
          <div className="relative mt-2 h-32 overflow-hidden rounded-xl border border-border bg-secondary/30" data-testid="widget-preview">
            <div className={`absolute bottom-3 flex flex-col gap-2 ${behavior.widget_position === "left" ? "left-3 items-start" : "right-3 items-end"}`}>
              <div className="rounded-2xl border border-border bg-background px-3 py-2 text-xs shadow-sm">Merhaba! Size nasıl yardımcı olabilirim?</div>
              <div style={{ backgroundColor: behavior.widget_color }} className="flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg">
                <MessageCircle className="h-5 w-5" />
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Footer */}
      <div className="sticky bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-background/90 px-4 py-3 backdrop-blur-xl">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5" />
          {published
            ? "Değişikliklerin yayına (hosted/API) yansıması için tekrar 'Yayınla'yın. Playground anında güncellenir."
            : "Playground bu ayarları anında kullanır."}
        </div>
        <Button data-testid="save-behavior-btn" onClick={save} disabled={saving} className="rounded-full">
          <Save className="mr-1.5 h-4 w-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
        </Button>
      </div>
    </div>
  );
}
